import SEO from '../components/SEO';
import { PageFooter } from '../components/UI';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'March 11, 2026';
const SITE = 'FinanceCalculator.cc';
const DOMAIN = 'financecalculator.cc';
const EMAIL = 'privacy@financecalculator.cc';

export default function PrivacyPolicy() {
  return (
    <div>
      <SEO
        title="Privacy Policy | FinanceCalculator.cc"
        description="Privacy Policy for FinanceCalculator.cc — learn how we handle your data, cookies, and advertising."
        canonical="https://financecalculator.cc/privacy"
      />

      <div style={{ background: 'var(--green)', color: 'white', padding: '48px 24px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7, marginBottom: 12 }}>Legal</div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 12 }}>Privacy Policy</h1>
          <p style={{ opacity: 0.75, fontSize: '0.95rem' }}>Effective date: {EFFECTIVE_DATE}</p>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '48px 24px 80px' }}>

        <div style={{ background: 'var(--green-pale)', border: '1px solid var(--green)', borderRadius: 'var(--radius-sm)', padding: '16px 20px', marginBottom: 40, fontSize: '0.9rem', color: 'var(--green)' }}>
          <strong>TL;DR:</strong> We don't collect your financial data — ever. All calculator inputs stay in your browser. We use Google AdSense (ads) and Google Analytics (traffic stats), which use cookies. You can opt out anytime.
        </div>

        <Section title="1. Who We Are">
          <p>{SITE} ("we", "us", or "our") operates the website at <strong>{DOMAIN}</strong>. We provide free financial calculators to help people make better money decisions.</p>
          <p>For privacy questions, contact us at: <a href={`mailto:${EMAIL}`} style={{ color: 'var(--green)' }}>{EMAIL}</a></p>
        </Section>

        <Section title="2. Information We Collect">
          <SubHeading>Financial calculator inputs</SubHeading>
          <p>All calculator inputs (income, rent, debt, savings, etc.) are processed <strong>entirely in your browser</strong>. This data is never sent to our servers, never stored, and never shared with anyone. It disappears when you close the tab.</p>

          <SubHeading>Automatically collected data</SubHeading>
          <p>When you visit our site, standard web server logs may automatically record:</p>
          <ul>
            <li>Your IP address (anonymized)</li>
            <li>Browser type and version</li>
            <li>Pages visited and time spent</li>
            <li>Referring website</li>
            <li>Date and time of visit</li>
          </ul>
          <p>This data is used only for security and performance monitoring, and is not linked to any personal identity.</p>

          <SubHeading>Cookies</SubHeading>
          <p>We use cookies through third-party services (described below). We do not set our own tracking cookies.</p>
        </Section>

        <Section title="3. Third-Party Services">
          <SubHeading>Google AdSense (Advertising)</SubHeading>
          <p>We use Google AdSense to display advertisements. Google may use cookies and similar technologies to show you personalized ads based on your browsing history across websites.</p>
          <p>You can opt out of personalized advertising at: <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green)' }}>google.com/settings/ads</a></p>
          <p>Google's privacy policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green)' }}>policies.google.com/privacy</a></p>

          <SubHeading>Google Analytics</SubHeading>
          <p>We use Google Analytics to understand how visitors use our site (which pages are popular, how long people stay, etc.). This data is aggregated and anonymous — we cannot identify individual users.</p>
          <p>Google Analytics uses cookies to track sessions. You can opt out using the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--green)' }}>Google Analytics Opt-out Browser Add-on</a>.</p>

          <SubHeading>Frankfurter API (Currency Rates)</SubHeading>
          <p>Our currency converter fetches live exchange rates from the Frankfurter API (European Central Bank data). This request includes your IP address as part of a standard HTTP request. No personal data is transmitted beyond what is technically necessary.</p>

          <SubHeading>Affiliate Links</SubHeading>
          <p>Some buttons on our site are affiliate links. If you click them and sign up for a product, we may earn a commission at no extra cost to you. We only recommend products we believe are genuinely useful. Affiliate partners may set their own cookies when you visit their sites.</p>
        </Section>

        <Section title="4. How We Use Your Information">
          <p>We use the limited data we collect to:</p>
          <ul>
            <li>Operate and improve the website</li>
            <li>Monitor site performance and security</li>
            <li>Understand which calculators are most useful</li>
            <li>Display relevant advertisements</li>
          </ul>
          <p>We do <strong>not</strong> sell your data. We do not build personal profiles. We do not send marketing emails.</p>
        </Section>

        <Section title="5. Your Rights (GDPR & CCPA)">
          <p>Depending on where you live, you may have the following rights regarding your personal data:</p>
          <ul>
            <li><strong>Right to access</strong> — request a copy of data we hold about you</li>
            <li><strong>Right to deletion</strong> — request that we delete your data</li>
            <li><strong>Right to opt out</strong> — opt out of personalized advertising (see Section 3)</li>
            <li><strong>Right to portability</strong> — receive your data in a portable format</li>
          </ul>
          <p>Since we collect minimal data and none of it is personally identifiable financial data, most requests can be fulfilled by clearing your browser cookies and opting out of Google personalized ads.</p>
          <p>For any requests, email: <a href={`mailto:${EMAIL}`} style={{ color: 'var(--green)' }}>{EMAIL}</a></p>
        </Section>

        <Section title="6. Data Retention">
          <p>We do not store your calculator inputs at any point. Server logs (IP addresses, page visits) are retained for a maximum of 90 days for security purposes, then automatically deleted. Google Analytics data is retained for 26 months per Google's standard settings.</p>
        </Section>

        <Section title="7. Children's Privacy">
          <p>{SITE} is not directed at children under 13. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will delete it immediately.</p>
        </Section>

        <Section title="8. Security">
          <p>Our site is served over HTTPS (encrypted). Since we don't store your financial data on our servers, there is no database of sensitive information that could be compromised. We use Vercel's infrastructure, which maintains industry-standard security practices.</p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. When we do, we'll update the effective date at the top of this page. Continued use of the site after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="10. Contact Us">
          <p>Questions about this Privacy Policy? Contact us at:</p>
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '16px 20px', marginTop: 12 }}>
            <strong>{SITE}</strong><br />
            <a href={`mailto:${EMAIL}`} style={{ color: 'var(--green)' }}>{EMAIL}</a>
          </div>
        </Section>

        <div style={{ marginTop: 48, paddingTop: 32, borderTop: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--muted)' }}>
          <Link to="/" style={{ color: 'var(--green)', fontWeight: 600, textDecoration: 'none' }}>← Back to calculators</Link>
        </div>
      </div>

      <PageFooter />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.3rem', fontWeight: 600, marginBottom: 16, color: 'var(--green)', letterSpacing: '-0.01em' }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: '0.925rem', lineHeight: 1.75, color: 'var(--text)' }}>
        {children}
      </div>
    </div>
  );
}

function SubHeading({ children }) {
  return <strong style={{ display: 'block', marginTop: 8, marginBottom: -4 }}>{children}</strong>;
}
